/*
  ============================================================
  SHYU COLLECTION CARD SWATCH / CUT LOGIC
  ============================================================

  Behaviour:

  COLOUR
  - Hover colour swatch:
      updates image
      updates title
      updates product URL
      updates available Cut labels

  CUT
  - Fixed display order:
      tab · quarter · crew
  - Only Cuts which exist for the active colourway are shown
  - Clicking Cut keeps the same colourway
  - Clicking Cut switches to that colourway's matching variant
  - Clicking Cut filters the visible colour swatches

  SWATCH FILTER
  - No variant in selected Cut -> hide colour swatch
  - Red line only if entire colourway is sold out

  SALE BADGE
  - Sale is assessed against the active Cut
  - Every AVAILABLE size within that Cut must be discounted
  - All discounted available sizes must have the same percentage
  - A discount in another Cut does not trigger the badge

  NAVIGATION
  - Clicking main or secondary sock image -> active product/variant
  - Clicking a colour swatch -> that colourway in active Cut
  - Product title continues to work normally

  NON-SOCK PRODUCTS
  - No Cut selector = normal swatch behaviour
*/


let eventListeners = [];
let preloadedImages = [];
let productGridObserver = null;


/* ============================================================
   CUT HELPERS
   ============================================================ */


function normaliseCut(value) {

    value =
        (value || '')
            .toLowerCase()
            .trim();


    if (value === 'quarter crew') {
        return 'quarter';
    }


    return value;
}



function getCutData(swatch, cut) {

    cut =
        normaliseCut(cut);


    if (!cut) {

        return {
            exists: false,
            available: false,
            url: '',
            image: ''
        };

    }


    return {

        exists:
            swatch.getAttribute(
                'data-has-' + cut
            ) === 'true',

        available:
            swatch.getAttribute(
                'data-' + cut + '-available'
            ) === 'true',

        url:
            swatch.getAttribute(
                'data-' + cut + '-url'
            ) || '',

        image:
            swatch.getAttribute(
                'data-' + cut + '-image'
            ) || ''

    };

}



function getCutsForSwatch(swatch) {

    const cuts = [];


    ['tab', 'quarter', 'crew']
        .forEach(function (cut) {

            if (
                getCutData(
                    swatch,
                    cut
                ).exists
            ) {

                cuts.push(cut);

            }

        });


    return cuts;

}



/* ============================================================
   ACTIVE SWATCH
   ============================================================ */


function getActiveSwatch(card) {

    return (
        card.querySelector(
            '.outer-circle.last-hovered .color-swatch'
        )
        ||
        card.querySelector(
            '.color-swatch'
        )
    );

}



function setActiveSwatch(card, swatch) {

    if (!card || !swatch) {
        return;
    }


    const previous =
        card.querySelector(
            '.outer-circle.last-hovered'
        );


    const next =
        swatch.closest(
            '.outer-circle'
        );


    if (
        previous &&
        previous !== next
    ) {

        previous.classList.remove(
            'last-hovered'
        );

    }


    if (next) {

        next.classList.add(
            'last-hovered'
        );

    }

}



/* ============================================================
   CUT SELECTOR
   ============================================================ */


function renderCutSelector(
    card,
    swatch,
    preferredCut
) {

    const selector =
        card.querySelector(
            '[data-shyu-cut-selector]'
        );


    if (!selector) {
        return;
    }


    const cuts =
        getCutsForSwatch(
            swatch
        );


    selector.innerHTML = '';


    if (!cuts.length) {

        selector.style.display =
            'none';

        delete card.dataset.activeCut;

        return;

    }


    selector.style.display =
        'flex';


    preferredCut =
        normaliseCut(
            preferredCut
        );


    let activeCut = '';


    if (
        preferredCut &&
        cuts.includes(preferredCut)
    ) {

        activeCut =
            preferredCut;

    }

    else if (
        cuts.includes('crew')
    ) {

        activeCut =
            'crew';

    }

    else if (
        cuts.includes('quarter')
    ) {

        activeCut =
            'quarter';

    }

    else {

        activeCut =
            cuts[0];

    }



    cuts.forEach(
        function (cut, index) {


            if (index > 0) {

                const divider =
                    document.createElement(
                        'span'
                    );


                divider.className =
                    'shyu-card-cut-divider';


                divider.textContent =
                    '·';


                divider.setAttribute(
                    'aria-hidden',
                    'true'
                );


                selector.appendChild(
                    divider
                );

            }



            const button =
                document.createElement(
                    'button'
                );


            button.type =
                'button';


            button.className =
                'shyu-card-cut-option';


            button.textContent =
                cut;


            button.setAttribute(
                'data-shyu-card-cut',
                ''
            );


            button.setAttribute(
                'data-cut-key',
                cut
            );


            if (
                cut === activeCut
            ) {

                button.classList.add(
                    'is-active'
                );

            }


            button.setAttribute(
                'aria-pressed',
                cut === activeCut
                    ? 'true'
                    : 'false'
            );


            selector.appendChild(
                button
            );

        }
    );


    card.dataset.activeCut =
        activeCut;

}



/* ============================================================
   FILTER SWATCHES FOR CUT
   ============================================================ */


function filterSwatchesForCut(
    card,
    cut
) {

    cut =
        normaliseCut(cut);


    const swatches =
        card.querySelectorAll(
            '.color-swatch'
        );


    if (!cut) {

        swatches.forEach(
            function (swatch) {

                const wrapper =
                    swatch.closest(
                        '.collection-product-card__swatch'
                    );


                if (wrapper) {

                    wrapper.style.display =
                        'inline-block';

                }

            }
        );


        return;

    }



    swatches.forEach(
        function (swatch) {


            const wrapper =
                swatch.closest(
                    '.collection-product-card__swatch'
                );


            if (!wrapper) {
                return;
            }


            const cutData =
                getCutData(
                    swatch,
                    cut
                );


            if (!cutData.exists) {

                wrapper.style.display =
                    'none';

                return;

            }


            wrapper.style.display =
                'inline-block';



            /*
              RED LINE:

              Only if the WHOLE colourway is sold out.
              Not merely the selected Cut.
            */

            const circle =
                swatch.closest(
                    '.outer-circle'
                );


            const productAvailable =
                swatch.getAttribute(
                    'data-product-available'
                ) === 'true';


            if (circle) {

                circle.classList.toggle(
                    'shyu-collection-sold-out',
                    !productAvailable
                );

            }

        }
    );

}



/* ============================================================
   IMAGE + TITLE + URL
   ============================================================ */


function updateCardForSwatch(
    card,
    swatch,
    cut
) {

    if (!card || !swatch) {
        return;
    }


    cut =
        normaliseCut(cut);


    const primaryImage =
        card.querySelector(
            '.--custom-primary-image'
        );


    const secondaryImage =
        card.querySelector(
            '.--custom-secondary-image'
        );


    const normalPrimaryImage =
        swatch.getAttribute(
            'data-image-url'
        );


    const normalSecondaryImage =
        swatch.getAttribute(
            'data-hover-image-url'
        );


    let targetImage =
        normalPrimaryImage;


    let targetUrl =
        swatch.getAttribute(
            'data-product-url'
        );


    if (cut) {

        const cutData =
            getCutData(
                swatch,
                cut
            );


        if (cutData.exists) {

            if (cutData.image) {

                targetImage =
                    cutData.image;

            }


            if (cutData.url) {

                targetUrl =
                    cutData.url;

            }

        }

    }



    if (
        primaryImage &&
        targetImage
    ) {

        primaryImage.src =
            targetImage;


        primaryImage.removeAttribute(
            'srcset'
        );

    }



    if (
        secondaryImage &&
        normalSecondaryImage
    ) {

        secondaryImage.src =
            normalSecondaryImage;


        secondaryImage.removeAttribute(
            'srcset'
        );

    }



    const productTitle =
        swatch.getAttribute(
            'data-product-title'
        );


    const productTitleAlt =
        swatch.getAttribute(
            'data-product-title-alt'
        );


    const titleToUse =
        (
            productTitleAlt &&
            productTitleAlt.trim() !== ''
        )
            ? productTitleAlt
            : productTitle;


    const titleLink =
        card.querySelector(
            'h3.title-linked-to-swatch a'
        );


    if (
        titleLink &&
        titleToUse
    ) {

        titleLink.textContent =
            titleToUse;

    }



    if (targetUrl) {

        card.dataset.currentProductUrl =
            targetUrl;


        if (titleLink) {

            titleLink.href =
                targetUrl;

        }


        const imageLink =
            card.querySelector(
                'h3.image-linked-to-swatch a'
            );


        if (imageLink) {

            imageLink.href =
                targetUrl;

        }


        const explicitImageLink =
            card.querySelector(
                '.shyu-card-image-link'
            );


        if (explicitImageLink) {

            explicitImageLink.href =
                targetUrl;

        }

    }



    handleBadge(
        card,
        swatch
    );

}



/* ============================================================
   CUT CLICK
   ============================================================ */


function applyCutToCurrentColour(
    card,
    cut
) {

    cut =
        normaliseCut(cut);


    const swatch =
        getActiveSwatch(
            card
        );


    if (!swatch) {
        return;
    }


    const cutData =
        getCutData(
            swatch,
            cut
        );


    if (!cutData.exists) {
        return;
    }


    card.dataset.activeCut =
        cut;



    card
        .querySelectorAll(
            '[data-shyu-card-cut]'
        )
        .forEach(
            function (button) {


                const active =
                    normaliseCut(
                        button.getAttribute(
                            'data-cut-key'
                        )
                    ) === cut;


                button.classList.toggle(
                    'is-active',
                    active
                );


                button.setAttribute(
                    'aria-pressed',
                    active
                        ? 'true'
                        : 'false'
                );

            }
        );



    filterSwatchesForCut(
        card,
        cut
    );



    updateCardForSwatch(
        card,
        swatch,
        cut
    );

}



/* ============================================================
   HOVER LISTENERS
   ============================================================ */


function initializeSwatchHoverListeners() {

    removeExistingListeners();


    const productCards =
        document.querySelectorAll(
            '.card-product-custom-div'
        );


    productCards.forEach(
        function (card) {


            const colorSwatches =
                card.querySelectorAll(
                    '.color-swatch'
                );


            const primaryImage =
                card.querySelector(
                    '.--custom-primary-image'
                );


            const secondaryImage =
                card.querySelector(
                    '.--custom-secondary-image'
                );


            if (!primaryImage) {
                return;
            }



            const existingImageLink =
                card.querySelector(
                    'h3.image-linked-to-swatch a'
                );


            const existingTitleLink =
                card.querySelector(
                    'h3.title-linked-to-swatch a'
                );


            if (
                existingImageLink &&
                existingImageLink.href
            ) {

                card.dataset.currentProductUrl =
                    existingImageLink.href;

            }

            else if (
                existingTitleLink &&
                existingTitleLink.href
            ) {

                card.dataset.currentProductUrl =
                    existingTitleLink.href;

            }



            let activeSwatch =
                getActiveSwatch(
                    card
                );


            if (
                !activeSwatch &&
                colorSwatches.length
            ) {

                activeSwatch =
                    colorSwatches[0];


                setActiveSwatch(
                    card,
                    activeSwatch
                );

            }



            const selector =
                card.querySelector(
                    '[data-shyu-cut-selector]'
                );


            if (
                selector &&
                activeSwatch
            ) {

                let initialCut =
                    normaliseCut(
                        selector.getAttribute(
                            'data-initial-cut'
                        )
                    );


                const activeCuts =
                    getCutsForSwatch(
                        activeSwatch
                    );


                if (
                    !activeCuts.includes(
                        initialCut
                    )
                ) {

                    if (
                        activeCuts.includes(
                            'crew'
                        )
                    ) {

                        initialCut =
                            'crew';

                    }

                    else if (
                        activeCuts.includes(
                            'quarter'
                        )
                    ) {

                        initialCut =
                            'quarter';

                    }

                    else if (
                        activeCuts.includes(
                            'tab'
                        )
                    ) {

                        initialCut =
                            'tab';

                    }

                }



                renderCutSelector(
                    card,
                    activeSwatch,
                    initialCut
                );



                filterSwatchesForCut(
                    card,
                    card.dataset.activeCut
                );



                updateCardForSwatch(
                    card,
                    activeSwatch,
                    card.dataset.activeCut
                );

            }



            colorSwatches.forEach(
                function (swatch) {


                    const primary =
                        swatch.getAttribute(
                            'data-image-url'
                        );


                    const secondary =
                        swatch.getAttribute(
                            'data-hover-image-url'
                        );


                    if (primary) {

                        preloadImage(
                            primary
                        );

                    }


                    if (secondary) {

                        preloadImage(
                            secondary
                        );

                    }



                    const handleMouseOver =
                        function () {


                            setActiveSwatch(
                                card,
                                swatch
                            );


                            const activeCut =
                                normaliseCut(
                                    card.dataset.activeCut
                                );


                            updateCardForSwatch(
                                card,
                                swatch,
                                activeCut
                            );



                            if (selector) {

                                renderCutSelector(
                                    card,
                                    swatch,
                                    activeCut
                                );

                            }

                        };


                    swatch.addEventListener(
                        'mouseover',
                        handleMouseOver
                    );


                    eventListeners.push({
                        element: swatch,
                        event: 'mouseover',
                        handler: handleMouseOver
                    });

                }
            );

        }
    );

}



/* ============================================================
   GLOBAL CLICK HANDLER
   ============================================================ */


function handleGlobalCollectionClick(event) {


    /*
      CUT
    */

    const cutButton =
        event.target.closest(
            '[data-shyu-card-cut]'
        );


    if (cutButton) {

        const card =
            cutButton.closest(
                '.card-product-custom-div'
            );


        if (!card) {
            return;
        }


        event.preventDefault();

        event.stopPropagation();


        applyCutToCurrentColour(
            card,
            cutButton.getAttribute(
                'data-cut-key'
            )
        );


        return;

    }



    /*
      SWATCH
    */

    const swatch =
        event.target.closest(
            '.color-swatch'
        );


    if (swatch) {

        const card =
            swatch.closest(
                '.card-product-custom-div'
            );


        if (!card) {
            return;
        }


        const activeCut =
            normaliseCut(
                card.dataset.activeCut
            );


        let destination =
            swatch.getAttribute(
                'data-product-url'
            );


        if (activeCut) {

            const cutData =
                getCutData(
                    swatch,
                    activeCut
                );


            if (
                cutData.exists &&
                cutData.url
            ) {

                destination =
                    cutData.url;

            }

        }


        if (destination) {

            event.preventDefault();

            event.stopPropagation();


            window.location.href =
                destination;

        }


        return;

    }



    /*
      IMAGE
    */

    const productImage =
        event.target.closest(
            '.--custom-primary-image, .--custom-secondary-image'
        );


    if (productImage) {

        const card =
            productImage.closest(
                '.card-product-custom-div'
            );


        if (!card) {
            return;
        }


        let destination =
            card.dataset.currentProductUrl;


        if (!destination) {

            const imageLink =
                card.querySelector(
                    'h3.image-linked-to-swatch a'
                );


            if (imageLink) {

                destination =
                    imageLink.href;

            }

        }


        if (!destination) {

            const titleLink =
                card.querySelector(
                    'h3.title-linked-to-swatch a'
                );


            if (titleLink) {

                destination =
                    titleLink.href;

            }

        }


        if (destination) {

            event.preventDefault();

            event.stopPropagation();


            window.location.href =
                destination;

        }


        return;

    }

}



/* ============================================================
   GRID OBSERVER
   ============================================================ */


function observeProductGridContainer() {

    const productGridContainer =
        document.getElementById(
            'ProductGridContainer'
        );


    if (!productGridContainer) {

        return;

    }


    if (productGridObserver) {

        productGridObserver.disconnect();

    }


    productGridObserver =
        new MutationObserver(
            function (mutationsList) {


                let cardStructureChanged =
                    false;


                mutationsList.forEach(
                    function (mutation) {


                        if (
                            mutation.type !==
                            'childList'
                        ) {

                            return;

                        }


                        mutation.addedNodes
                            .forEach(
                                function (node) {


                                    if (
                                        node.nodeType !==
                                        1
                                    ) {

                                        return;

                                    }


                                    if (
                                        node.matches &&
                                        node.matches(
                                            '.card-product-custom-div'
                                        )
                                    ) {

                                        cardStructureChanged =
                                            true;

                                    }


                                    else if (
                                        node.querySelector &&
                                        node.querySelector(
                                            '.card-product-custom-div'
                                        )
                                    ) {

                                        cardStructureChanged =
                                            true;

                                    }

                                }
                            );

                    }
                );


                if (cardStructureChanged) {

                    window.requestAnimationFrame(
                        function () {

                            initializeSwatchHoverListeners();

                        }
                    );

                }

            }
        );


    productGridObserver.observe(
        productGridContainer,
        {
            childList: true,
            subtree: true
        }
    );

}



/* ============================================================
   CLEAN UP HOVER LISTENERS
   ============================================================ */


function removeExistingListeners() {

    eventListeners.forEach(
        function (listener) {

            listener.element
                .removeEventListener(
                    listener.event,
                    listener.handler
                );

        }
    );


    eventListeners = [];

}



/* ============================================================
   IMAGE PRELOAD
   ============================================================ */


function preloadImage(url) {

    if (!url) {
        return;
    }


    const image =
        new Image();


    image.src =
        url;


    preloadedImages.push(
        image
    );

}



/* ============================================================
   BADGES
   ============================================================ */


function handleBadge(
    card,
    swatch
) {

    const badgeElement =
        card.querySelector(
            '.card__badge .badge'
        );


    if (!badgeElement) {
        return;
    }


    const isAvailable =
        swatch.getAttribute(
            'data-product-available'
        ) === 'true';



    /*
      Use active Cut-specific sale data when it exists.

      This prevents a Quarter discount from making
      Crew incorrectly show "50% OFF", and vice versa.
    */

    const activeCut =
        normaliseCut(
            card.dataset.activeCut
        );


    const cutSaleAttribute =
        activeCut
            ? swatch.getAttribute(
                'data-' + activeCut + '-on-sale'
            )
            : null;


    const hasCutSaleData =
        cutSaleAttribute !== null;


    const isOnSale =
        hasCutSaleData
            ? cutSaleAttribute === 'true'
            : swatch.getAttribute(
                'data-on-sale'
            ) === 'true';


    const discountPercentage =
        parseInt(
            hasCutSaleData
                ? swatch.getAttribute(
                    'data-' + activeCut + '-discount-percentage'
                ) || '0'
                : swatch.getAttribute(
                    'data-discount-percentage'
                ) || '0',
            10
        );



    const customBadgeText =
        swatch.getAttribute(
            'data-custom-badge'
        );


    const publishedAtValue =
        swatch.getAttribute(
            'data-published-at'
        );


    const publishedAt =
        publishedAtValue
            ? new Date(
                publishedAtValue
            )
            : null;


    const customBadgeColorScheme =
        swatch.getAttribute(
            'data-custom-badge-color-scheme'
        );


    const soldOutBadgeColorScheme =
        swatch.getAttribute(
            'data-sold-out-badge-color-scheme'
        );


    const newInBadgeColorScheme =
        swatch.getAttribute(
            'data-new-in-badge-color-scheme'
        );


    const saleBadgeColorScheme =
        swatch.getAttribute(
            'data-sale-badge-color-scheme'
        );


    const productPrice =
        parseFloat(
            swatch.getAttribute(
                'data-product-price'
            )
        );


    const productComparePrice =
        parseFloat(
            swatch.getAttribute(
                'data-product-compare-at-price'
            )
        );


    const fourteenDaysAgo =
        new Date();


    fourteenDaysAgo.setDate(
        fourteenDaysAgo.getDate() - 14
    );



    if (customBadgeText) {

        badgeElement.textContent =
            customBadgeText;


        badgeElement.className =
            'badge badge--bottom-left color-' +
            customBadgeColorScheme;


        badgeElement.style.display =
            '';

    }


    else if (!isAvailable) {

        badgeElement.textContent =
            'Sold out';


        badgeElement.className =
            'badge badge--bottom-left color-' +
            soldOutBadgeColorScheme;


        badgeElement.style.display =
            '';

    }


    else if (
        publishedAt &&
        !isNaN(
            publishedAt.getTime()
        ) &&
        publishedAt >= fourteenDaysAgo
    ) {

        badgeElement.textContent =
            'New In';


        badgeElement.className =
            'badge badge--bottom-left color-' +
            newInBadgeColorScheme;


        badgeElement.style.display =
            '';

    }


    else if (
        isOnSale &&
        discountPercentage > 0 &&
        (
            hasCutSaleData ||
            productComparePrice > productPrice
        )
    ) {

        badgeElement.textContent =
            discountPercentage +
            '% OFF';


        badgeElement.className =
            'badge badge--bottom-left color-' +
            saleBadgeColorScheme;


        badgeElement.style.display =
            '';

    }


    else {

        badgeElement.style.display =
            'none';

    }

}



/* ============================================================
   START
   ============================================================ */


function initializePage() {

    initializeSwatchHoverListeners();

    observeProductGridContainer();

}



if (
    !window.shyuCollectionGlobalClickInstalled
) {

    document.addEventListener(
        'click',
        handleGlobalCollectionClick,
        true
    );


    window.shyuCollectionGlobalClickInstalled =
        true;

}



if (
    document.readyState ===
    'loading'
) {

    document.addEventListener(
        'DOMContentLoaded',
        initializePage,
        {
            once: true
        }
    );

}

else {

    initializePage();

}